import { memo, useMemo } from "react";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { MultiSelect } from "@/components/ui/multi-select";
import { Label } from "@/components/ui/label";
import { FormFieldProps } from "./types";
import { Separator } from "@/components/ui/separator";
import { useModifiableList } from "@/api/modifiableList";
import { useGetAuthorsForMultiSelect } from "@/api/author/author";
import { getMainLanguage } from "@/i18n";

const MultiSelectField = memo(({ config, control }: FormFieldProps) => {
  const checkIfArray = (value: any) => {
    const result = Array.isArray(value) ? value : value?.split(",");
    if (Array.isArray(result) && result.length === 1 && result[0] === "") {
      return [];
    }
    return result;
  };

  const { data: options } = useModifiableList(config.list, getMainLanguage());
  const { data: authors } = useGetAuthorsForMultiSelect(
    config.type,
    config.linkType,
  );

  const finalOptions = useMemo(() => {
    if (config.type === "link-simple" && config.linkType === "artist") {
      return authors?.map((author) => ({
        label:
          author.artistesAuteursDescriptionPrenom +
          " " +
          author.artistesAuteursDescriptionNom,
        value:
          author.artistesAuteursDescriptionPrenom +
          " " +
          author.artistesAuteursDescriptionNom,
      }));
    }
    return options;
  }, [authors, options, config]);

  return (
    <>
      <FormField
        control={control}
        name={config.name}
        render={({ field }) => (
          <FormItem className={config.className}>
            <FormControl>
              <div className="flex flex-col gap-1">
                <Label
                  htmlFor={field.name}
                  className="text-slate-700 text-left text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {config.label}
                </Label>
                <MultiSelect
                  options={finalOptions ?? []}
                  listKey={config.list}
                  onValueChange={(e) => field.onChange(e.join(","))}
                  defaultValue={checkIfArray(field.value)}
                  placeholder={config.placeholder}
                  variant="default"
                  maxCount={config.maxKey ?? 3}
                  id={field.name}
                  {...field}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
      {config.separator && <Separator className="my-4" />}
    </>
  );
});

MultiSelectField.displayName = "MultiSelectField";
export default MultiSelectField;
